import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { StarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { testimonials, Testimonial } from '../data/content';
const AUTOPLAY_DELAY = 4500;
function Stars() {
  return <div className="flex gap-0.5" aria-label="5 de 5 estrellas" data-id="element-207" data-mp="3b0aw05jisjk0">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className="h-[18px] w-[18px] fill-primary text-primary" aria-hidden="true" data-id="element-208" data-mp="36wwiblj20f9q" data-mpi={i} />)}
    </div>;
}
function Card({
  t
}: {
  t: Testimonial;
}) {
  return <article className="flex h-full flex-col rounded-2xl border border-line bg-surface p-6 shadow-card" data-id="element-209" data-mp="2x1l2ezvwt20e">
      <Stars data-id="element-210" data-mp="2s39reltvolgl" />
      <p className="mt-4 flex-1 text-sm leading-relaxed text-foreground/85" data-id="element-211" data-mp="2s3t8y1fm97u3">“{t.text}”</p>
      <div className="mt-6 flex items-center gap-3" data-id="element-212" data-mp="v8ncpcxfipjo">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-surface" data-id="element-213" data-mp="q9u15q1pd8m5">
          {t.initials}
        </span>
        <div data-id="element-214" data-mp="m51dpz4onjbl">
          <p className="text-sm font-bold text-foreground" data-id="element-215" data-mp="m4roidrgpg6y">{t.name}</p>
          {t.city && <p className="text-xs text-muted" data-id="element-216" data-mp="m2opj48c0cq3">{t.city}</p>}
          {t.role && <p className="text-xs italic text-primary-strong" data-id="element-217" data-mp="79bqjxnrh05a">{t.role}</p>}
        </div>
      </div>
    </article>;
}
export function Testimonials() {
  const prefersReducedMotion = useMemo(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);
  const plugins = useMemo(() => prefersReducedMotion ? [] : [Autoplay({
    delay: AUTOPLAY_DELAY,
    stopOnMouseEnter: true,
    stopOnInteraction: false
  })], [prefersReducedMotion]);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    duration: 28
  }, plugins);
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState<number[]>([]);
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    const onReInit = () => {
      setSnaps(emblaApi.scrollSnapList());
      onSelect();
    };
    onReInit();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onReInit);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onReInit);
    };
  }, [emblaApi]);
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);
  return <section className="w-full bg-background px-5 py-12 sm:py-20" data-id="element-218" data-mp="14sk0qt8gt9ac">
      <div className="mx-auto max-w-5xl" data-id="element-219" data-mp="10rqcfyr41sgx">
        <div className="mx-auto max-w-2xl text-center" data-id="element-220" data-mp="10srmq10ayaxl">
          <h2 className="font-display text-2xl font-bold leading-tight text-foreground sm:text-4xl" data-id="element-221" data-mp="vsnf70hdgqji">
            Lo que dicen quienes ya <span className="text-pink" data-id="element-222" data-mp="qtai7mwse337">empezaron</span>
          </h2>
          <p className="mt-3 text-sm text-muted sm:text-base" data-id="element-223" data-mp="32gw80qdg8whp">
            <span className="tracking-wider text-primary" aria-label="5 de 5 estrellas" data-id="element-224" data-mp="32ge4r9sddi0a">★★★★★</span> 4.9/5 · Más de 500
            personas ya empezaron
          </p>
        </div>

        <div className="mt-10" role="region" aria-roledescription="carrusel" aria-label="Testimonios" data-id="element-225" data-mp="3cg4uky5zj8u1">
          <div ref={emblaRef} className="cursor-grab overflow-hidden active:cursor-grabbing" data-id="element-226" data-mp="3gfdrud08houc">
            <div className="-ml-6 flex touch-pan-y py-1" data-id="element-227" data-mp="2jrpk8oxstep1">
              {testimonials.map((t, i) => <div key={t.name} role="group" aria-roledescription="testimonio" aria-label={`${i + 1} de ${testimonials.length}`} className="min-w-0 shrink-0 grow-0 basis-full pl-6 md:basis-1/2 lg:basis-1/3" data-id="element-228" data-mp="2oopnks39k3cg" data-mpi={i}>
                  <Card t={t} data-id="element-229" data-mp="2ximawa85zltg" />
                </div>)}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-4" data-id="element-230" data-mp="210q0c255uv2m">
            <button onClick={scrollPrev} aria-label="Testimonio anterior" className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-primary transition-colors duration-150 hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary" data-id="element-231" data-mp="2519y97xk62pt">
              <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-232" data-mp="1vmg9h9q3v0hl" />
            </button>

            <div className="flex flex-wrap justify-center gap-2" data-id="element-233" data-mp="1qvzwfqq75gvr">
              {snaps.map((_, i) => <button key={i} onClick={() => scrollTo(i)} aria-label={`Ir al grupo de testimonios ${i + 1}`} aria-current={i === selected} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === selected ? 'w-6 bg-primary' : 'w-2 bg-primary/30 hover:bg-primary/50'}`} data-id="element-234" data-mp="1qu5802fpv6mx" data-mpi={i} />)}
            </div>

            <button onClick={scrollNext} aria-label="Testimonio siguiente" className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-primary transition-colors duration-150 hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary" data-id="element-235" data-mp="29ly4i6tzv9ze">
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-236" data-mp="2qva5pwxvygl7" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}